import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useCampusPass } from '../hooks/useCampusPass';
import { useSession } from '../hooks/useSession';
import { dayMonth } from '../utils/format';
import { ChoiceRow, PassStatusCard } from '../components/Growth';
import { PhotoField } from '../components/PhotoField';
import {
  Button,
  Card,
  DraftBar,
  EmptyState,
  Field,
  ListSkeleton,
  Notice,
  PageHeader,
  SectionTitle,
} from '../components/ui';

const kmText = (km: number) => (Number.isInteger(km) ? String(km) : km.toFixed(1));

/**
 * Campus Pass (W18): find your campus (GPS or the delivery point), apply with a college email
 * code or a student ID photo, then see the pass — pending, active with its expiry, or rejected
 * with the reason. The form is draft-saved; the email code is never saved.
 */
export function CampusPassPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const session = useSession();
  const c = useCampusPass(session.signedIn, {
    emailBad: t('campus.emailBad'),
    tooBig: t('campus.photoTooBig'),
    sent: t('campus.sent'),
    verified: t('campus.verified'),
    uploaded: t('campus.uploaded'),
    failed: t('campus.failed'),
  });
  const pass = c.pass;
  const heroKm = c.campus?.freeKm ?? c.campuses[0]?.freeKm ?? 1.5;

  const reapply = (
    <Button tone="dark" full onClick={c.reapply}>
      {t('campus.tryAgain')}
    </Button>
  );

  return (
    <div className="min-h-dvh bg-page pb-10">
      <PageHeader
        title={t('campus.title')}
        onBack={() => navigate(-1)}
        backLabel={t('common.back')}
      />
      <div className="flex flex-col gap-5 px-4 pt-4">
        {c.failed ? (
          <Notice
            tone="negative"
            supportRef
            title={t('common.wrong')}
            action={
              <Button size="sm" tone="outline" onClick={c.retry}>
                {t('common.retry')}
              </Button>
            }
          >
            {c.error}
          </Notice>
        ) : c.loading || !c.data ? (
          <Card>
            <ListSkeleton rows={4} tall />
          </Card>
        ) : (
          <>
            {c.step !== 'status' && (
              <PassStatusCard
                tone="positive"
                icon="star"
                label={t('campus.title')}
                title={t('campus.hero')}
                lines={[t('campus.heroBody', { km: kmText(heroKm), days: c.passDays })]}
              />
            )}

            {c.formError && <Notice tone="negative" title={c.formError} />}

            {c.step === 'status' && pass && (
              <PassStatusCard
                tone={
                  pass.status === 'active'
                    ? 'positive'
                    : pass.status === 'pending_review'
                      ? 'warning'
                      : pass.status === 'rejected'
                        ? 'negative'
                        : 'neutral'
                }
                icon={
                  pass.status === 'active' ? 'shield' : pass.status === 'rejected' ? 'ban' : 'clock'
                }
                label={pass.campus.name}
                title={t(`campus.status.${pass.status}`)}
                lines={
                  pass.status === 'active'
                    ? [
                        t('campus.activeBody', {
                          km: kmText(pass.campus.freeKm),
                          campus: pass.campus.name,
                        }),
                        ...(pass.validUntil
                          ? [t('campus.validUntil', { date: dayMonth(pass.validUntil) })]
                          : []),
                      ]
                    : pass.status === 'pending_review'
                      ? [t('campus.reviewBody', { campus: pass.campus.name })]
                      : pass.status === 'rejected'
                        ? pass.rejectionReason
                          ? [t('campus.rejectedBody', { reason: pass.rejectionReason })]
                          : []
                        : [t('campus.expiredBody', { campus: pass.campus.name })]
                }
                action={
                  pass.status === 'rejected' || pass.status === 'expired' ? reapply : undefined
                }
              />
            )}

            {c.step === 'code' && pass && (
              <Card className="space-y-4">
                <div>
                  <p className="text-base font-bold text-ink">{t('campus.codeTitle')}</p>
                  <p className="mt-1 text-xs text-ink-2">
                    {t('campus.codeBody', { email: pass.email ?? '' })}
                  </p>
                </div>
                <Field
                  label={t('campus.code')}
                  value={c.code}
                  onChange={(event) => c.setCode(event.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  className="tabular"
                />
                <Button full busy={c.verifying} disabled={c.code.length !== 6} onClick={c.verify}>
                  {t('campus.verify')}
                </Button>
                <Button tone="ghost" full onClick={c.reapply}>
                  {t('campus.startOver')}
                </Button>
              </Card>
            )}

            {c.step === 'photo' && (
              <Card className="space-y-4">
                <div>
                  <p className="text-base font-bold text-ink">{t('campus.photoTitle')}</p>
                  <p className="mt-1 text-xs text-ink-2">{t('campus.photoBody')}</p>
                </div>
                <PhotoField
                  labels={{
                    title: t('campus.methodPhoto'),
                    hint: t('campus.photoTooBig'),
                    add: t('campus.photoPick'),
                    replace: t('campus.photoReplace'),
                    remove: t('campus.photoRemove'),
                    uploading: (percent) => t('campus.photoUploading', { percent }),
                  }}
                  url={c.photoPreview}
                  uploading={c.progress}
                  onFile={c.choosePhoto}
                  onRemove={c.clearPhoto}
                />
                <Button full busy={c.uploading} disabled={!c.canUpload} onClick={c.upload}>
                  {t('campus.photoUpload')}
                </Button>
                <Button tone="ghost" full onClick={c.reapply}>
                  {t('campus.startOver')}
                </Button>
              </Card>
            )}

            {c.step === 'apply' && (
              <>
                {c.draftRestored && (
                  <DraftBar
                    text={t('common.draftRestored')}
                    keepLabel={t('common.keep')}
                    discardLabel={t('common.discard')}
                    onKeep={c.keepDraft}
                    onDiscard={c.discardDraft}
                  />
                )}

                <section>
                  <SectionTitle
                    action={
                      <Button size="sm" tone="ghost" busy={c.locating} onClick={c.locate}>
                        {c.locating ? t('campus.locating') : t('campus.useLocation')}
                      </Button>
                    }
                  >
                    {t('campus.pick')}
                  </SectionTitle>
                  {c.campuses.length === 0 ? (
                    <Card>
                      <EmptyState icon="map" title={t('campus.noCampus')}>
                        {t('campus.noCampusBody')}
                      </EmptyState>
                    </Card>
                  ) : (
                    <Card flush>
                      <div
                        role="radiogroup"
                        aria-label={t('campus.pick')}
                        className="divide-y divide-hair"
                      >
                        {c.campuses.map((campus) => (
                          <ChoiceRow
                            key={campus.id}
                            icon="map"
                            title={campus.name}
                            sub={[
                              campus.townName,
                              campus.distanceKm !== null
                                ? t('campus.distance', { km: kmText(campus.distanceKm) })
                                : null,
                              t('campus.freeWithin', { km: kmText(campus.freeKm) }),
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                            picked={c.form.campusId === campus.id}
                            onPick={() => c.pick(campus.id)}
                          />
                        ))}
                      </div>
                    </Card>
                  )}
                </section>

                {c.campus && (
                  <>
                    <section>
                      <SectionTitle>{t('campus.how')}</SectionTitle>
                      <Card flush>
                        <div
                          role="radiogroup"
                          aria-label={t('campus.how')}
                          className="divide-y divide-hair"
                        >
                          {c.campus.emailDomains.length > 0 && (
                            <ChoiceRow
                              icon="mail"
                              title={t('campus.methodEmail')}
                              sub={t('campus.methodEmailHint')}
                              picked={c.form.method === 'email'}
                              onPick={() => c.setMethod('email')}
                            />
                          )}
                          <ChoiceRow
                            icon="camera"
                            title={t('campus.methodPhoto')}
                            sub={t('campus.methodPhotoHint')}
                            picked={
                              c.form.method === 'id_photo' || c.campus.emailDomains.length === 0
                            }
                            onPick={() => c.setMethod('id_photo')}
                          />
                        </div>
                      </Card>
                    </section>

                    <Card className="space-y-4">
                      {c.form.method === 'email' && c.campus.emailDomains.length > 0 && (
                        <Field
                          label={t('campus.email')}
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          value={c.form.email}
                          onChange={(event) => c.setEmail(event.target.value)}
                          placeholder={`name@${c.campus.emailDomains[0] ?? 'college.edu'}`}
                          hint={t('campus.emailHint', {
                            domains: c.campus.emailDomains.map((domain) => `@${domain}`).join(', '),
                          })}
                          error={c.emailError}
                        />
                      )}
                      <Field
                        label={t('campus.ambassador')}
                        value={c.form.ambassadorCode}
                        onChange={(event) => c.setAmbassadorCode(event.target.value)}
                        autoCapitalize="characters"
                        autoComplete="off"
                        maxLength={16}
                      />
                      <Button
                        full
                        busy={c.starting}
                        disabled={!c.canStart}
                        onClick={() => {
                          if (
                            c.campus &&
                            c.campus.emailDomains.length === 0 &&
                            c.form.method === 'email'
                          ) {
                            c.setMethod('id_photo');
                          }
                          c.start();
                        }}
                      >
                        {c.form.method === 'email' && c.campus.emailDomains.length > 0
                          ? t('campus.sendCode')
                          : t('campus.continue')}
                      </Button>
                    </Card>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
